import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { Peeker } from "./ShadowMark";
import "./styles/demo-modal.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";

// Per-CTA copy. The `context` label that opens the modal also picks the heading and
// the primary-action label, so "Talk to us" and "Get on the list" keep their wording
// instead of all becoming "Book a demo". Everything else falls back to the demo copy.
const VARIANTS = {
  "Team tier": {
    title: "Talk to us",
    sub: "Shadow for your whole team. Tell us about your reps and we'll set you up.",
    cta: "Start the conversation",
  },
  Enterprise: {
    title: "Get on the list",
    sub: "Get on the list and we'll keep you posted.",
    cta: "Join the list",
  },
};
const DEFAULT_COPY = {
  title: "Book a demo",
  sub: "See Shadow run live on your calls. We'll get you set up.",
  cta: "Request demo",
};

const DemoModalContext = createContext(null);

// Any site CTA calls `openDemoModal({ context })` to pop the form. `context` is a short
// label (e.g. "Team tier", "Nav") that rides along as `use_case` into Slack + PostHog so
// we know which CTA the lead came from, and selects the copy variant above.
export function useDemoModal() {
  const ctx = useContext(DemoModalContext);
  if (!ctx) throw new Error("useDemoModal must be used within <DemoModalProvider>");
  return ctx;
}

export function DemoModalProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [context, setContext] = useState(null);

  const openDemoModal = useCallback((opts = {}) => {
    setContext(opts.context || null);
    setOpen(true);
  }, []);
  const closeDemoModal = useCallback(() => setOpen(false), []);

  return (
    <DemoModalContext.Provider value={{ openDemoModal, closeDemoModal }}>
      {children}
      {open && <DemoModal context={context} onClose={closeDemoModal} />}
    </DemoModalContext.Provider>
  );
}

function DemoModal({ context, onClose }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [company, setCompany] = useState("");
  const [status, setStatus] = useState("idle"); // idle | submitting | done | duplicate | error
  const nameRef = useRef(null);

  const copy = VARIANTS[context] || DEFAULT_COPY;

  // Focus the first field on open; close on Escape; lock background scroll.
  useEffect(() => {
    nameRef.current?.focus();
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  const canSubmit = name.trim() && email.trim() && status !== "submitting";

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setStatus("submitting");
    try {
      const res = await fetch(`${BACKEND_URL}/api/platform/waitlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim() || null,
          company: company.trim() || null,
          use_case: context ? `${copy.title} · ${context}` : copy.title,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setStatus(data.status === "already_registered" ? "duplicate" : "done");
    } catch (err) {
      setStatus("error");
    }
  };

  return (
    <div className="demo-modal-overlay" onMouseDown={onClose}>
      <div
        className="demo-modal"
        role="dialog"
        aria-modal="true"
        aria-label={copy.title}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <button className="demo-modal-close" onClick={onClose} aria-label="Close">
          &times;
        </button>

        {status === "done" || status === "duplicate" ? (
          <div className="demo-modal-done">
            <div className="demo-done-crest">
              <span className="demo-done-rule" />
              <Peeker className="demo-done-peeker" />
            </div>
            <div className="demo-done-check" aria-hidden="true">&#10003;</div>
            {status === "duplicate" ? (
              <>
                <h3>Already got you.</h3>
                <p>We have <b>{email.trim()}</b> on file. We&rsquo;ll be in touch soon.</p>
              </>
            ) : (
              <>
                <h3>You&rsquo;re on the list.</h3>
                <p>
                  Thanks{name ? `, ${name.trim().split(" ")[0]}` : ""}. We&rsquo;ll reach out at{" "}
                  <b>{email.trim()}</b> to set you up.
                </p>
              </>
            )}
            <button className="btn btn-dark demo-modal-submit" onClick={onClose}>Done</button>
          </div>
        ) : (
          <form className="demo-modal-form" onSubmit={submit}>
            <h3>{copy.title}</h3>
            <p className="demo-modal-sub">{copy.sub}</p>

            <label className="demo-field">
              <span>Name</span>
              <input
                ref={nameRef}
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Jane Doe"
                required
              />
            </label>

            <label className="demo-field">
              <span>Work email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="jane@company.com"
                required
              />
            </label>

            <div className="demo-field-row">
              <label className="demo-field">
                <span>Phone <i className="demo-opt">optional</i></span>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+1 555 000 1234"
                />
              </label>
              <label className="demo-field">
                <span>Company <i className="demo-opt">optional</i></span>
                <input
                  type="text"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  placeholder="Acme Inc"
                />
              </label>
            </div>

            {status === "error" && (
              <div className="demo-modal-error">Something went wrong. Try again, or email hello@shadowlabs.ai.</div>
            )}

            <button className="btn btn-dark demo-modal-submit" type="submit" disabled={!canSubmit}>
              {status === "submitting" ? "Sending…" : copy.cta}
            </button>
            <p className="demo-modal-fineprint">we only use this to reach out about your demo</p>
          </form>
        )}
      </div>
    </div>
  );
}
